import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import parse from 'html-react-parser';
import { _ } from '_locales';

import { Drawer } from '_components';
import { isRTL, eventTracker } from '_helpers';

import Loader from './Loader';
import useMaterialDetails from './hooks';
import { Image, Description } from './components';
import { LoadingSpinner } from '../../components';

/**
 * import styles
 */
const parentWidth = document.getElementById('rcroot').getBoundingClientRect().width;

const Title = styled.h2`
  font-size: 16px !important;
  display: block;
   
  direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
  margin-bottom: 7px !important;
  text-align: left;
`;

const DepotContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 35px 0;
`;

const Drawers = styled.ul`
  margin: 30px 0;
  padding: 0;
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  text-align: center;
  margin: 0 auto;
  position: relative;
  background-color: #fff;
  padding: 0;

  &.content-container-responsive {
    width: calc(100% - 195px);
  }
`;

const ContentContainer = styled.div`
  ${(parentWidth < 551) && css`
    .content-container-responsive {
      width: 100% !important;
      padding: 0 20px !important;
      box-sizing: border-box !important;
    }
  `};
`;
// End of styles

/**
 * This component is for preventing the details of materials
 *
 * @author Alireza Zebardast
 * @param {object} plugin
 * @param {object} config
 * @param {object} location
 */
const MaterialDetails = ({ plugin, config, props }) => {
  const [depotLinks, setDepotLinks] = useState([]);
  const [trimmedDetailLinks, setTrimmedDetailLinks] = useState([]);

  const { projectId } = plugin;
  const { selectedLang, districtId } = config;

  const { location } = props;
  const { matId } = location.state;

  const { details, error, loading } = useMaterialDetails(
    projectId, districtId, matId, selectedLang
  );

  /**
     * Event tracker
     */
  useEffect(() => {
    if (details && details.id) {
      eventTracker('CMS - Material Page', details.id);
    }
  }, [details]);

  useEffect(() => {
    if (
      details
      && Object.keys(details).length
    ) {
      const tempDetailLinks = [...details.links];

      const depotEventsObjs = tempDetailLinks
        .filter(({ href, hrefPart, type }) => ((
          href
          && href.toLowerCase().includes('drop-depot')
        ) || (
            href
            && href.toLowerCase().includes('drop-event')
          ) || (
            hrefPart
            && hrefPart.toLowerCase().includes('drop-depot')
          ) || (
            hrefPart
            && hrefPart.toLowerCase().includes('drop-event')
          )) && type === 'remote');
      setDepotLinks(depotEventsObjs);

      for (let i = 0; i < depotEventsObjs.length; i += 1) {
        const index = tempDetailLinks.findIndex((item) => item === depotEventsObjs[i]);
        tempDetailLinks.splice(index, 1);
      }

      setTrimmedDetailLinks(tempDetailLinks);
    }
  }, [details]);

  return (
    <Container>
      {
        loading ? (<LoadingSpinner height={500} />) : (
          <>
            {
              !error
                && details
                && Object.keys(details).length
                ? (
                  <ContentContainer>
                    {/* <CTAofCMS /> */}
                    {details.images
                      && details.images.length
                      ? (
                        <Image images={details.images} alt={`${details.name} image`} />
                      ) : null}

                    <Container className="content-container-responsive">
                      <Title>{parse(details.disposal_header)}</Title>
                      <Title style={{ marginTop: '0', color: '#595959', fontWeight: '500' }}>{details.name}</Title>
                      <Description>
                        {details.disposal_body}
                      </Description>
                      {
                        details.coll_id
                          || depotLinks.length ? (
                          <DepotContainer>
                            {
                              details.coll_id
                                ? (
                                  <Drawer
                                    link={{ drawer_title: _('collectionRequirements.collection_requirements'), type: 'remote', hrefPart: `collection/${details.coll_id}.page` }}
                                    lang={selectedLang}
                                    flag="collReq"
                                  />
                                ) : null
                            }
                            {
                              depotLinks.map((link) => (
                                <Drawer
                                  key={link.drawer_title}
                                  link={link}
                                  lang={selectedLang}
                                  flag={(link.drawer_title.toLowerCase().includes('locations')) ? 'depot' : 'event'}
                                />
                              ))
                            }
                          </DepotContainer>
                        ) : null
                      }

                      {
                        details.links.length
                          ? (
                            <Drawers>
                              {
                                trimmedDetailLinks.map((link) => (
                                  <li style={{ listStyle: 'none' }}>
                                    <Drawer
                                      key={link.drawer_title}
                                      link={link}
                                      lang={selectedLang}
                                    />
                                  </li>
                                ))
                              }
                            </Drawers>
                          ) : null
                      }
                    </Container>
                  </ContentContainer>
                ) : null
            }
          </>
        )
      }
    </Container>
  );
};

export default MaterialDetails;
